/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Migrated from: lego/page/etmf/list/apply-package/step1/package-contents/AddFileplanArtifactModal.jsx
 *
 *
 * Modal wrapper for EditPackageStep2 to add fileplan artifacts to a package.
 * Uses custom Modal component and delegates to existing EditPackageStep2.
 */

import React, { useEffect, useState } from 'react'
import Modal from '../../../../Modal'
import { useServiceContext } from '../../../../../root'
import EditPackageStep2 from '../../../EditPackageStep2'
import type { PackageData, PackageOptions, SelectedFilters } from '../../../EditPackageStep2/types'

interface AddFileplanArtifactModalProps {
  packageData: PackageData | null
  data: any[]
  addFileplans: (entries: any[] | undefined) => void
  show: boolean
  onClose: () => void
}

const AddFileplanArtifactModal: React.FC<AddFileplanArtifactModalProps> = ({
  packageData,
  data,
  addFileplans,
  show,
  onClose
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [optionsData, setOptionsData] = useState<PackageOptions>({})
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    rule: [],
    stage: [],
    milestone: []
  })
  const [manuallySelectedArtifactIds, setManuallySelectedArtifactIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (data) {
      const filePlanLevel3EntriesData = context.api.packages.getLevel3Entries(data)
      setOptionsData((optsData) => ({
        ...optsData,
        fileplanEntries: data,
        filePlanLevel3Entries: filePlanLevel3EntriesData
      }))
    }
  }, [data, context.api.packages])

  const handleClose = () => {
    onClose()
  }

  const handleNextStep = (packageDataRef: PackageData, optionsDataRef: PackageOptions, nextStepItem: string) => {
    // CRITICAL: Only use the second argument (optionsDataRef) as per legacy logic
    setOptionsData(optionsDataRef)
    addFileplans(optionsDataRef?.selectedFPEntries)
  }

  const handleSetPackageData = (data: PackageData | ((prev: PackageData) => PackageData)) => {
    // No-op setPackageData - not used in apply package flow but required by EditPackageStep2
    // Not used in this modal context
  }

  return (
    <Modal
      show={show}
      handleClose={handleClose}
      title={translate('SETTINGS_PACKAGE_ARTIFACT_ADD_TITLE')}
      size="lg"
      className="level-two-modal edit-packages-wrapper"
    >
      <EditPackageStep2
        packageData={packageData}
        options={optionsData}
        isApplyPackageFlow={true}
        nextStep={handleNextStep}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        setPackageData={handleSetPackageData}
        manuallySelectedArtifactIds={manuallySelectedArtifactIds}
        setManuallySelectedArtifactIds={setManuallySelectedArtifactIds}
        doClose={handleClose}
      />
    </Modal>
  )
}

export default AddFileplanArtifactModal
